(() => {
  const swatchSelector = '[data-product-card-swatch]';
  const swatchExpandSelector = '[data-product-card-swatches-expand]';
  const swatchTooltipSelector = '[data-swatch-tooltip]';
  const mediaShellSelector = '.product-card__media-shell';
  const minimumSwatchLoadingMs = 250;
  const productCache = new Map();
  const imagePreloadCache = new Map();
  const carouselState = new WeakMap();
  const carouselMediaOverrides = new WeakMap();
  let swatchRequestId = 0;
  let carouselSwipe = null;
  let suppressedCarouselClickTarget = null;
  let carouselClickSuppressTimer = null;
  let activeTooltipTarget = null;
  let swatchTooltip = null;
  let tooltipFrame = null;

  const delay = (duration) => {
    return new Promise((resolve) => {
      window.setTimeout(resolve, duration);
    });
  };

  const formatMoney = (cents) => {
    const currency = window.Shopify?.currency?.active || 'USD';

    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency
      }).format(Number(cents || 0) / 100);
    } catch (error) {
      return `$${(Number(cents || 0) / 100).toFixed(2)}`;
    }
  };

  const formatSwatchTooltip = (value) => {
    if (window.WobSwatches?.getSwatchLabel) {
      return window.WobSwatches.getSwatchLabel(value);
    }

    return String(value || '')
      .replace(/[-_]+/g, ' ')
      .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/\s+/g, ' ')
      .trim();
  };

  const formatSwatchTooltips = (root = document) => {
    const tooltipElements = [];

    if (root instanceof Element && root.matches(swatchTooltipSelector)) {
      tooltipElements.push(root);
    }

    if (root.querySelectorAll) {
      tooltipElements.push(...root.querySelectorAll(swatchTooltipSelector));
    }

    tooltipElements.forEach((element) => {
      const rawTooltip = element.dataset.swatchTooltipRaw || element.dataset.swatchTooltip;

      if (!rawTooltip) return;

      element.dataset.swatchTooltipRaw = rawTooltip;
      element.dataset.swatchTooltip = formatSwatchTooltip(rawTooltip);
    });
  };

  const getTooltipElement = () => {
    if (swatchTooltip) return swatchTooltip;

    swatchTooltip = document.createElement('div');
    swatchTooltip.id = 'SwatchTooltip';
    swatchTooltip.className = 'swatch-tooltip';
    swatchTooltip.setAttribute('role', 'tooltip');
    swatchTooltip.hidden = true;
    document.body.appendChild(swatchTooltip);

    return swatchTooltip;
  };

  const getTooltipTarget = (target) => {
    return target instanceof Element ? target.closest(swatchTooltipSelector) : null;
  };

  const restoreTooltipDescription = (target) => {
    if (!target) return;

    const previousDescription = target.dataset.swatchTooltipPreviousDescription || '';

    if (previousDescription) {
      target.setAttribute('aria-describedby', previousDescription);
    } else {
      target.removeAttribute('aria-describedby');
    }

    delete target.dataset.swatchTooltipPreviousDescription;
  };

  const positionSwatchTooltip = () => {
    tooltipFrame = null;

    if (!activeTooltipTarget || !swatchTooltip || swatchTooltip.hidden) return;

    const tooltip = swatchTooltip;
    const targetRect = activeTooltipTarget.getBoundingClientRect();
    const viewportWidth = document.documentElement.clientWidth;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const viewportPadding = 8;
    const tooltipOffset = 10;

    if (!targetRect.width && !targetRect.height) {
      tooltip.hidden = true;
      return;
    }

    tooltip.style.left = '0px';
    tooltip.style.top = '0px';

    const tooltipRect = tooltip.getBoundingClientRect();
    const targetCenter = targetRect.left + (targetRect.width / 2);
    let left = targetCenter - (tooltipRect.width / 2);
    let top = targetRect.top - tooltipRect.height - tooltipOffset;
    let placement = 'top';

    left = Math.min(Math.max(left, viewportPadding), viewportWidth - tooltipRect.width - viewportPadding);

    if (top < viewportPadding) {
      top = targetRect.bottom + tooltipOffset;
      placement = 'bottom';
    }

    if (top + tooltipRect.height > viewportHeight - viewportPadding) {
      top = Math.max(viewportPadding, viewportHeight - tooltipRect.height - viewportPadding);
    }

    const arrowX = Math.min(Math.max(targetCenter - left, 10), tooltipRect.width - 10);

    tooltip.dataset.placement = placement;
    tooltip.style.left = `${Math.round(left)}px`;
    tooltip.style.top = `${Math.round(top)}px`;
    tooltip.style.setProperty('--swatch-tooltip-arrow-x', `${Math.round(arrowX)}px`);
  };

  const scheduleTooltipPosition = () => {
    if (!activeTooltipTarget || !swatchTooltip || swatchTooltip.hidden) return;
    if (tooltipFrame) return;

    tooltipFrame = window.requestAnimationFrame(positionSwatchTooltip);
  };

  const showSwatchTooltip = (target) => {
    const tooltipText = target?.dataset?.swatchTooltip || '';

    if (!tooltipText) return;

    if (activeTooltipTarget && activeTooltipTarget !== target) {
      restoreTooltipDescription(activeTooltipTarget);
    }

    const tooltip = getTooltipElement();
    const currentDescription = target.getAttribute('aria-describedby') || '';

    activeTooltipTarget = target;
    tooltip.textContent = tooltipText;
    tooltip.hidden = false;
    tooltip.classList.add('is-visible');

    if (!Object.prototype.hasOwnProperty.call(target.dataset, 'swatchTooltipPreviousDescription')) {
      target.dataset.swatchTooltipPreviousDescription = currentDescription;
    }

    const describedBy = currentDescription.split(/\s+/).filter(Boolean);

    if (!describedBy.includes(tooltip.id)) describedBy.push(tooltip.id);

    target.setAttribute('aria-describedby', describedBy.join(' '));
    scheduleTooltipPosition();
  };

  const hideSwatchTooltip = (target = activeTooltipTarget) => {
    if (!swatchTooltip || !activeTooltipTarget || target !== activeTooltipTarget) return;

    restoreTooltipDescription(activeTooltipTarget);
    activeTooltipTarget = null;
    swatchTooltip.classList.remove('is-visible');
    swatchTooltip.hidden = true;
  };

  const getProductJsonPath = (url) => {
    if (!url) return '';

    try {
      const parsedUrl = new URL(url, window.location.origin);
      const productMatch = parsedUrl.pathname.match(/\/products\/([^/]+)/);
      const handle = productMatch?.[1]?.replace(/\.js$/, '');

      if (!handle) return '';
      return `/products/${handle}.js`;
    } catch (error) {
      return '';
    }
  };

  const withImageWidth = (url, width) => {
    if (!url) return '';

    const normalizedUrl = String(url).startsWith('//') ? `${window.location.protocol}${url}` : String(url);

    try {
      const parsedUrl = new URL(normalizedUrl, window.location.origin);
      parsedUrl.searchParams.set('width', String(width));
      return parsedUrl.toString();
    } catch (error) {
      return normalizedUrl;
    }
  };

  const buildSrcset = (url) => {
    return [320, 540, 760]
      .map((width) => `${withImageWidth(url, width)} ${width}w`)
      .join(', ');
  };

  const imageIdentityKey = (url) => {
    if (!url) return '';

    const normalizedUrl = String(url).startsWith('//') ? `${window.location.protocol}${url}` : String(url);

    try {
      const parsedUrl = new URL(normalizedUrl, window.location.origin);
      parsedUrl.searchParams.delete('width');
      return parsedUrl.toString();
    } catch (error) {
      return normalizedUrl.replace(/([?&])width=\d+(&?)/, '$1').replace(/[?&]$/, '');
    }
  };

  const fetchProduct = async (url) => {
    const productJsonPath = getProductJsonPath(url);

    if (!productJsonPath) return null;
    if (productCache.has(productJsonPath)) return productCache.get(productJsonPath);

    const request = fetch(productJsonPath, {
      headers: { Accept: 'application/json' }
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Unable to fetch ${productJsonPath}`);
        return response.json();
      })
      .catch(() => null);

    productCache.set(productJsonPath, request);
    return request;
  };

  const getVariantImage = (product, url) => {
    let variantImage = '';

    try {
      const selectedVariantId = new URL(url, window.location.origin).searchParams.get('variant');
      const selectedVariant = product?.variants?.find((variant) => String(variant.id) === selectedVariantId);
      variantImage = selectedVariant?.featured_image?.src || selectedVariant?.featured_image || '';
    } catch (error) {
      variantImage = '';
    }

    return variantImage || product?.featured_image || product?.images?.[0] || '';
  };

  const getHoverImage = (product, primaryImage) => {
    const images = Array.isArray(product?.images) ? product.images : [];

    return images.find((image) => String(image) !== String(primaryImage)) || primaryImage;
  };

  const getSwatchImage = async (swatch) => {
    const imageSrc = swatch.dataset.imageSrc;

    if (imageSrc) {
      return {
        alt: swatch.dataset.imageAlt || '',
        hoverAlt: swatch.dataset.hoverImageAlt || swatch.dataset.imageAlt || '',
        hoverSrc: swatch.dataset.hoverImageSrc || imageSrc,
        hoverSrcset: swatch.dataset.hoverImageSrcset || swatch.dataset.imageSrcset || '',
        src: imageSrc,
        srcset: swatch.dataset.imageSrcset || ''
      };
    }

    if (swatch.dataset.productCardImageMissing === 'true') return null;

    const product = await fetchProduct(swatch.dataset.productCardUrl);
    const productImage = getVariantImage(product, swatch.dataset.productCardUrl);
    const hoverImage = getHoverImage(product, productImage);

    if (!productImage) return null;

    return {
      alt: product?.title || swatch.getAttribute('aria-label') || '',
      hoverAlt: product?.title || swatch.getAttribute('aria-label') || '',
      hoverSrc: withImageWidth(hoverImage, 540),
      hoverSrcset: buildSrcset(hoverImage),
      src: withImageWidth(productImage, 540),
      srcset: buildSrcset(productImage)
    };
  };

  const preloadImage = ({ sizes = '', src = '', srcset = '' }) => {
    if (!src) return Promise.resolve();

    const cacheKey = [src, srcset, sizes].join('|');

    if (imagePreloadCache.has(cacheKey)) return imagePreloadCache.get(cacheKey);

    const request = new Promise((resolve) => {
      const image = new Image();

      image.onload = resolve;
      image.onerror = resolve;
      if (sizes) image.sizes = sizes;
      if (srcset) image.srcset = srcset;
      image.src = src;
    });

    imagePreloadCache.set(cacheKey, request);
    return request;
  };

  const preloadSwatchImages = (card, imageData) => {
    if (!imageData?.src) return Promise.resolve();

    const primaryImage = card.querySelector('[data-product-card-primary-image]');
    const sizes = primaryImage?.sizes || primaryImage?.getAttribute('sizes') || '';

    return Promise.all([
      preloadImage({
        sizes,
        src: imageData.src,
        srcset: imageData.srcset
      }),
      preloadImage({
        sizes,
        src: imageData.hoverSrc || imageData.src,
        srcset: imageData.hoverSrcset || imageData.srcset
      })
    ]);
  };

  const markImageLoaded = (image) => {
    image.classList.remove('image-loaded');
    image.classList.remove('image-loading');
    image.classList.add('image-loaded');
  };

  const setImageSource = (image, { alt = '', src = '', srcset = '' }) => {
    if (!image || !src) return;

    const currentSrc = image.currentSrc || image.src || '';
    const currentSrcset = image.getAttribute('srcset') || '';
    const nextSrcset = srcset || '';
    const hasSameSource = imageIdentityKey(currentSrc) === imageIdentityKey(src) && currentSrcset === nextSrcset;

    if (hasSameSource) {
      image.alt = alt || image.alt;
      image.hidden = false;
      markImageLoaded(image);
      return;
    }

    if (srcset) {
      image.srcset = srcset;
    } else {
      image.removeAttribute('srcset');
    }

    image.src = src;
    image.alt = alt || image.alt;
    image.hidden = false;
    markImageLoaded(image);
  };

  const updateCardLinks = (card, url) => {
    if (!url) return;

    card.querySelectorAll('[data-product-card-link]').forEach((link) => {
      link.setAttribute('href', url);
    });
  };

  const normalizeColorToken = (value) => {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[_\s]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const getColorMatchTokens = (value) => {
    const normalized = normalizeColorToken(value);

    if (!normalized) return [];

    return [
      normalized,
      normalized.replace(/-/g, ' '),
      normalized.replace(/-/g, '_')
    ];
  };

  const normalizeMediaImage = (image) => {
    const url = image?.url || image?.src || '';

    if (!url) return null;

    return {
      altText: image.altText || image.alt || '',
      height: image.height || null,
      url,
      width: image.width || null
    };
  };

  const uniqueImages = (images) => {
    const seen = new Set();

    return images.map(normalizeMediaImage).filter((image) => {
      const imageKey = imageIdentityKey(image?.url);

      if (!image?.url || seen.has(imageKey)) return false;
      seen.add(imageKey);
      return true;
    });
  };

  const getProductMediaImages = (product) => {
    const mediaImages = (Array.isArray(product?.media) ? product.media : []).map((media) => ({
      altText: media?.alt || '',
      height: media?.preview_image?.height || media?.height || null,
      url: media?.preview_image?.src || media?.src || '',
      width: media?.preview_image?.width || media?.width || null
    }));
    const productImages = (Array.isArray(product?.images) ? product.images : []).map((image) => ({
      altText: '',
      url: typeof image === 'string' ? image : image?.src || image?.url || ''
    }));

    return uniqueImages([...mediaImages, ...productImages]);
  };

  const imageMatchesColor = (image, color) => {
    const alt = String(image?.altText || image?.alt || '').toLowerCase();
    const tokens = getColorMatchTokens(color);

    return Boolean(alt && tokens.length && tokens.some((token) => alt.includes(token)));
  };

  const getCardMediaPayload = (card) => {
    const script = card.querySelector('[data-product-card-media]');

    if (!script?.textContent) return { images: [], groups: [], truncated: false };

    try {
      const parsed = JSON.parse(script.textContent);

      if (Array.isArray(parsed)) {
        return { images: parsed, groups: [], truncated: false };
      }

      return {
        groups: Array.isArray(parsed.groups) ? parsed.groups : [],
        images: Array.isArray(parsed.images) ? parsed.images : [],
        truncated: Boolean(parsed.truncated)
      };
    } catch (error) {
      return { images: [], groups: [], truncated: false };
    }
  };

  const getImageFromElement = (image) => {
    if (!image?.currentSrc && !image?.src) return null;

    return {
      altText: image.alt || '',
      height: image.naturalHeight || Number(image.getAttribute('height')) || null,
      url: image.currentSrc || image.src,
      width: image.naturalWidth || Number(image.getAttribute('width')) || null
    };
  };

  const getImageFromSwatchDataset = (swatch, prefix = '') => {
    const url = prefix ? swatch?.dataset?.[`${prefix}ImageSrc`] : swatch?.dataset?.imageSrc;

    if (!url) return null;

    return {
      altText: prefix ? swatch.dataset[`${prefix}ImageAlt`] || '' : swatch.dataset.imageAlt || '',
      url
    };
  };

  const getActiveColor = (card) => {
    return card.querySelector(`${swatchSelector}.product-card__swatch--active`)?.dataset.productCardColorValue || '';
  };

  const getPayloadColorImages = (card, color) => {
    const payload = getCardMediaPayload(card);
    const normalizedColor = normalizeColorToken(color);
    const baseImages = uniqueImages(payload.images || []);
    const activeGroup = (payload.groups || []).find((group) => {
      return normalizeColorToken(group.color) === normalizedColor;
    });
    const groupedImages = activeGroup ? uniqueImages(activeGroup.images || []) : [];

    if (groupedImages.length) return groupedImages;
    if (!color) return baseImages;

    return baseImages.filter((image) => imageMatchesColor(image, color));
  };

  const hydrateCarouselMedia = async (card, swatch) => {
    const color = swatch?.dataset.productCardColorValue || '';
    const swatchUrl = swatch?.dataset.productCardUrl || '';
    const payload = getCardMediaPayload(card);
    const payloadMayBeTruncated = payload.truncated || payload.images.length === 20;

    if (
      !card
      || !color
      || !swatchUrl
      || (getPayloadColorImages(card, color).length > 1 && !payloadMayBeTruncated)
    ) {
      carouselMediaOverrides.delete(card);
      return;
    }

    const product = await fetchProduct(swatchUrl);
    const colorImages = getProductMediaImages(product).filter((image) => imageMatchesColor(image, color));

    if (colorImages.length > 1) {
      carouselMediaOverrides.set(card, {
        color: normalizeColorToken(color),
        images: colorImages,
        swatchUrl
      });
    } else {
      carouselMediaOverrides.delete(card);
    }
  };

  const getCardCarouselImages = (card) => {
    const payload = getCardMediaPayload(card);
    const activeColor = getActiveColor(card);
    const normalizedActiveColor = normalizeColorToken(activeColor);
    const activeSwatch = card.querySelector(`${swatchSelector}.product-card__swatch--active`);
    const primaryImage = card.querySelector('[data-product-card-primary-image]');
    const secondaryImage = card.querySelector('.product-card__secondary-image');
    const baseImages = uniqueImages(payload.images || []);
    const activeGroup = (payload.groups || []).find((group) => {
      return normalizeColorToken(group.color) === normalizedActiveColor;
    });
    const mediaOverride = carouselMediaOverrides.get(card);
    const overrideMatches = mediaOverride
      && mediaOverride.color === normalizedActiveColor
      && mediaOverride.swatchUrl === (activeSwatch?.dataset.productCardUrl || '');
    const groupedImages = overrideMatches
      ? uniqueImages(mediaOverride.images || [])
      : (activeGroup ? uniqueImages(activeGroup.images || []) : []);
    const matchedImages = activeColor
      ? baseImages.filter((image) => imageMatchesColor(image, activeColor))
      : baseImages;
    const colorImages = groupedImages.length ? groupedImages : matchedImages;
    const fallbackImages = [
      getImageFromElement(primaryImage),
      getImageFromElement(secondaryImage),
      getImageFromSwatchDataset(activeSwatch),
      getImageFromSwatchDataset(activeSwatch, 'hover')
    ];
    const images = uniqueImages([
      ...fallbackImages,
      ...colorImages,
      ...(!activeColor && !colorImages.length ? baseImages : [])
    ]);

    return images;
  };

  const getCarouselImageSource = (image) => {
    if (!image?.url) return null;

    return {
      alt: image.altText || '',
      height: image.height || null,
      src: withImageWidth(image.url, 540),
      srcset: buildSrcset(image.url),
      width: image.width || null
    };
  };

  const updateCarouselControls = (card) => {
    const state = carouselState.get(card);
    const controls = card.querySelector('[data-product-card-carousel-controls]');
    const mediaShell = card.querySelector(mediaShellSelector);
    const hasCarousel = Boolean(state?.images?.length > 1);

    if (!controls || !mediaShell) return;

    controls.hidden = !hasCarousel;
    mediaShell.classList.toggle('product-card__media-shell--carousel', hasCarousel);
  };

  const preloadCarouselImages = (card) => {
    const state = carouselState.get(card);
    const primaryImage = card.querySelector('[data-product-card-primary-image]');
    const sizes = primaryImage?.sizes || primaryImage?.getAttribute('sizes') || '';

    if (!state?.images?.length) return;

    state.images.slice(1).forEach((image) => {
      const source = getCarouselImageSource(image);
      if (!source) return;

      preloadImage({
        sizes,
        src: source.src,
        srcset: source.srcset
      });
    });
  };

  const prepareCarousel = (card, { reset = false } = {}) => {
    const activeColor = getActiveColor(card);
    const existing = carouselState.get(card);

    if (!reset && existing && existing.color === activeColor) {
      updateCarouselControls(card);
      preloadCarouselImages(card);
      return existing;
    }

    const images = getCardCarouselImages(card);
    const currentImage = card.querySelector('[data-product-card-primary-image]');
    const currentUrl = imageIdentityKey(currentImage?.currentSrc || currentImage?.src || '');
    const currentIndex = Math.max(0, images.findIndex((image) => {
      return image.url && currentUrl && imageIdentityKey(image.url) === currentUrl;
    }));
    const state = {
      color: activeColor,
      images,
      index: currentIndex
    };

    carouselState.set(card, state);
    updateCarouselControls(card);
    preloadCarouselImages(card);

    return state;
  };

  const setCarouselImage = async (card, nextIndex) => {
    const state = prepareCarousel(card);

    if (!state?.images?.length) return;

    const imageCount = state.images.length;
    const normalizedIndex = ((nextIndex % imageCount) + imageCount) % imageCount;
    const imageSource = getCarouselImageSource(state.images[normalizedIndex]);
    const primaryImage = card.querySelector('[data-product-card-primary-image]');

    if (!imageSource || !primaryImage) return;

    const sizes = primaryImage.sizes || primaryImage.getAttribute('sizes') || '';

    await preloadImage({
      sizes,
      src: imageSource.src,
      srcset: imageSource.srcset
    });

    state.index = normalizedIndex;
    setImageSource(primaryImage, imageSource);
    updateCarouselControls(card);
  };

  const suppressNextCarouselClick = (mediaLink) => {
    suppressedCarouselClickTarget = mediaLink || null;
    window.clearTimeout(carouselClickSuppressTimer);
    carouselClickSuppressTimer = window.setTimeout(() => {
      suppressedCarouselClickTarget = null;
    }, 500);
  };

  const startCarouselSwipe = (event) => {
    if (event.pointerType !== 'touch') return;

    const mediaLink = event.target instanceof Element ? event.target.closest('.product-card__media') : null;
    const card = mediaLink?.closest('.product-card');
    const state = card ? prepareCarousel(card) : null;

    if (!mediaLink || !card || !state?.images || state.images.length <= 1) return;

    carouselSwipe = {
      card,
      mediaLink,
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      isHorizontal: false
    };

    try {
      mediaLink.setPointerCapture(event.pointerId);
    } catch (error) {
      // Pointer capture can fail on some browser-generated events; swipe still resolves on pointerup.
    }
  };

  const moveCarouselSwipe = (event) => {
    if (!carouselSwipe || carouselSwipe.pointerId !== event.pointerId) return;

    const deltaX = event.clientX - carouselSwipe.startX;
    const deltaY = event.clientY - carouselSwipe.startY;

    if (!carouselSwipe.isHorizontal && Math.abs(deltaX) >= 10 && Math.abs(deltaX) > Math.abs(deltaY) * 1.15) {
      carouselSwipe.isHorizontal = true;
    }

    if (carouselSwipe.isHorizontal && event.cancelable) {
      event.preventDefault();
    }
  };

  const endCarouselSwipe = (event) => {
    if (!carouselSwipe || carouselSwipe.pointerId !== event.pointerId) return;

    const swipe = carouselSwipe;
    const deltaX = event.clientX - swipe.startX;
    const deltaY = event.clientY - swipe.startY;
    const isHorizontalSwipe = Math.abs(deltaX) >= 38 && Math.abs(deltaX) > Math.abs(deltaY) * 1.25;

    carouselSwipe = null;

    if (!isHorizontalSwipe) return;

    const state = prepareCarousel(swipe.card);
    if (!state?.images || state.images.length <= 1) return;

    if (event.cancelable) event.preventDefault();
    suppressNextCarouselClick(swipe.mediaLink);
    void setCarouselImage(swipe.card, state.index + (deltaX < 0 ? 1 : -1));
  };

  const cancelCarouselSwipe = (event) => {
    if (!carouselSwipe || carouselSwipe.pointerId !== event.pointerId) return;

    carouselSwipe = null;
  };

  const resetCarousel = (card) => {
    const state = prepareCarousel(card, { reset: true });

    if (state) {
      state.index = 0;
      updateCarouselControls(card);
    }
  };

  const setCardImageLoading = (card, isLoading) => {
    const media = card.querySelector('.product-card__media');

    if (!media) return;

    media.classList.toggle('product-card__media--swatch-loading', isLoading);
    media.setAttribute('aria-busy', String(isLoading));
  };

  const getVariantOptions = (variant) => {
    if (Array.isArray(variant?.options)) return variant.options;

    return [variant?.option1, variant?.option2, variant?.option3].filter((option) => option !== null && option !== undefined);
  };

  const getVariantOption = (variant, index) => {
    return getVariantOptions(variant)[index] || '';
  };

  const normalizeOptionValue = (value) => {
    return String(value || '').trim().toLowerCase();
  };

  const getOptionName = (option) => {
    return typeof option === 'string' ? option : option?.name || '';
  };

  const findOptionIndex = (options, matcher) => {
    if (!Array.isArray(options)) return -1;

    return options.findIndex((option) => matcher(getOptionName(option).toLowerCase()));
  };

  const getCardTitle = (card) => {
    return card.querySelector('.product-card__title')?.textContent?.trim() || 'Product';
  };

  const getCardVariants = (card) => {
    const script = card.querySelector('[data-product-card-variants]');

    if (!script?.textContent) return [];

    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      return [];
    }
  };

  const getCardVariantSource = (card) => {
    return {
      colorIndex: Number(card.dataset.productCardColorOptionIndex || -1),
      sizeIndex: Number(card.dataset.productCardSizeOptionIndex || -1),
      title: getCardTitle(card),
      variants: getCardVariants(card)
    };
  };

  const getProductVariantSource = (product) => {
    const options = product?.options || [];

    return {
      colorIndex: findOptionIndex(options, (name) => name.includes('color') || name.includes('colour')),
      sizeIndex: findOptionIndex(options, (name) => name.includes('size')),
      title: product?.title || 'Product',
      variants: product?.variants || []
    };
  };

  const getVariantById = (variants, id) => {
    if (!id) return null;

    return variants.find((variant) => String(variant.id) === String(id)) || null;
  };

  const getFirstAvailableVariant = (variants) => {
    return variants.find((variant) => !variant.hidden && variant.available) || variants.find((variant) => !variant.hidden) || null;
  };

  const findVariantForSize = ({ colorIndex, selectedColorValue = '', sizeIndex, sizeValue, variants }) => {
    const normalizedSizeValue = normalizeOptionValue(sizeValue);
    const normalizedColorValue = normalizeOptionValue(selectedColorValue);

    return variants.find((variant) => {
      if (variant.hidden) return false;
      if (normalizeOptionValue(getVariantOption(variant, sizeIndex)) !== normalizedSizeValue) return false;
      if (colorIndex < 0 || !normalizedColorValue) return true;

      return normalizeOptionValue(getVariantOption(variant, colorIndex)) === normalizedColorValue;
    }) || null;
  };

  const updateDirectQuickAdd = (card, variant, title) => {
    const directInput = card.querySelector('.product-card__quick-form [data-product-card-variant-input]');
    const directButton = card.querySelector('.product-card__quick-form button[type="submit"]');

    if (!directInput || !directButton || !variant?.id) return;

    directInput.value = variant.id;
    directButton.disabled = !variant.available;
    directButton.setAttribute('aria-label', variant.available ? `Add ${title} to bag` : `${title} unavailable`);
  };

  const updateCardPrice = (card, variant) => {
    const currentPriceCents = Number(variant?.price);
    const compareAtPriceCents = Number(variant?.compare_at_price || 0);

    if (!Number.isFinite(currentPriceCents)) return;

    if (window.WobPromotionPricing?.updateVariant) {
      card.querySelectorAll('.product-card__details [data-price]').forEach((price) => {
        window.WobPromotionPricing.updateVariant(price, {
          productId: card.dataset.productId,
          variantId: variant.id,
          priceCents: currentPriceCents,
          compareAtPriceCents
        });
      });

      const saleBadge = card.querySelector('.product-card__badge--sale');

      if (saleBadge) {
        const onSale = Number.isFinite(compareAtPriceCents) && compareAtPriceCents > currentPriceCents;
        const salePercent = onSale
          ? Math.round(((compareAtPriceCents - currentPriceCents) / compareAtPriceCents) * 100)
          : 0;

        saleBadge.textContent = `${salePercent}% off`;
        saleBadge.hidden = !onSale;
      }

      return;
    }

    card.querySelectorAll('.product-card__details [data-price]').forEach((price) => {
      const currentPrice = price.querySelector('[data-price-current]');
      const currentLabel = price.querySelector('[data-price-current-label]');
      const onSale = Number.isFinite(compareAtPriceCents) && compareAtPriceCents > currentPriceCents;
      let compareLabel = price.querySelector('[data-price-compare-label]');
      let comparePrice = price.querySelector('[data-price-compare]');
      let discountLabel = price.querySelector('[data-price-discount]');

      if (currentPrice) currentPrice.textContent = formatMoney(currentPriceCents);
      if (currentLabel) currentLabel.textContent = onSale ? 'Sale price' : 'Regular price';
      price.classList.toggle('price--on-sale', onSale);

      if (onSale) {
        if (!compareLabel) {
          compareLabel = document.createElement('span');
          compareLabel.className = 'visually-hidden';
          compareLabel.dataset.priceCompareLabel = '';
          compareLabel.textContent = 'Regular price';
          price.append(compareLabel);
        }

        if (!comparePrice) {
          comparePrice = document.createElement('s');
          comparePrice.className = 'price__compare';
          comparePrice.dataset.priceCompare = '';
          price.append(comparePrice);
        }

        comparePrice.textContent = formatMoney(compareAtPriceCents);

        const salePercent = Math.round(((compareAtPriceCents - currentPriceCents) / compareAtPriceCents) * 100);
        if (!discountLabel) {
          discountLabel = document.createElement('span');
          discountLabel.className = 'price__discount';
          discountLabel.dataset.priceDiscount = '';
          price.append(discountLabel);
        }
        discountLabel.textContent = `${salePercent}% off`;
        discountLabel.setAttribute('aria-label', `${salePercent} percent off`);
        price.append(discountLabel);
      } else {
        compareLabel?.remove();
        comparePrice?.remove();
        discountLabel?.remove();
      }

      const saleBadge = card.querySelector('.product-card__badge--sale');

      if (saleBadge) {
        const salePercent = onSale
          ? Math.round(((compareAtPriceCents - currentPriceCents) / compareAtPriceCents) * 100)
          : 0;

        saleBadge.textContent = `${salePercent}% off`;
        saleBadge.hidden = !onSale;
      }
    });
  };

  const updateSizeQuickAdd = (card, source, selectedColorValue = '') => {
    const sizeForms = Array.from(card.querySelectorAll('[data-product-card-size-form]'));

    if (!sizeForms.length) {
      updateDirectQuickAdd(card, getFirstAvailableVariant(source.variants), source.title);
      return;
    }

    if (source.sizeIndex < 0) {
      sizeForms.forEach((form) => {
        const input = form.querySelector('[data-product-card-variant-input]');
        const button = form.querySelector('[data-product-card-size-button]');
        const sizeValue = form.dataset.productCardSizeValue || '';

        if (input) input.value = '';
        if (button) {
          button.disabled = true;
          button.setAttribute('aria-label', `${source.title} - ${sizeValue} unavailable`);
        }
      });
      return;
    }

    sizeForms.forEach((form) => {
      const input = form.querySelector('[data-product-card-variant-input]');
      const button = form.querySelector('[data-product-card-size-button]');
      const sizeValue = form.dataset.productCardSizeValue || '';
      const variant = findVariantForSize({
        colorIndex: source.colorIndex,
        selectedColorValue,
        sizeIndex: source.sizeIndex,
        sizeValue,
        variants: source.variants
      });

      if (!input || !button) return;

      input.value = variant?.id || '';
      button.disabled = !variant?.available;
      button.setAttribute(
        'aria-label',
        variant?.available ? `Add ${source.title} - ${sizeValue} to bag` : `${source.title} - ${sizeValue} unavailable`
      );
    });
  };

  const updateQuickAddForSwatch = async (card, swatch, requestId) => {
    const isCurrentRequest = () => card.dataset.productCardSwatchRequest === requestId;

    if (swatch.dataset.productCardVariantId) {
      const source = getCardVariantSource(card);
      const selectedVariant = getVariantById(source.variants, swatch.dataset.productCardVariantId);

      if (!isCurrentRequest()) return;
      updateSizeQuickAdd(card, source, swatch.dataset.productCardColorValue);
      updateDirectQuickAdd(card, selectedVariant, source.title);
      updateCardPrice(card, selectedVariant);
      return;
    }

    const product = await fetchProduct(swatch.dataset.productCardUrl);

    if (!isCurrentRequest()) return;
    if (!product) return;

    const source = getProductVariantSource(product);
    const selectedVariantId = new URL(swatch.dataset.productCardUrl, window.location.origin).searchParams.get('variant');
    const selectedVariant = getVariantById(source.variants, selectedVariantId) || getFirstAvailableVariant(source.variants);

    updateSizeQuickAdd(card, source);
    updateDirectQuickAdd(card, selectedVariant, source.title);
    updateCardPrice(card, selectedVariant);
  };

  const syncQuickAddFormVariant = (form) => {
    const card = form.closest('.product-card');
    const activeSwatch = card?.querySelector(`${swatchSelector}.product-card__swatch--active`);
    const selectedVariantId = activeSwatch?.dataset.productCardVariantId;
    const input = form.querySelector('[data-product-card-variant-input]');

    if (!card || !selectedVariantId || !input) return;

    const source = getCardVariantSource(card);
    const sizeValue = form.dataset.productCardSizeValue || '';
    const variant = sizeValue
      ? findVariantForSize({
          colorIndex: source.colorIndex,
          selectedColorValue: activeSwatch.dataset.productCardColorValue,
          sizeIndex: source.sizeIndex,
          sizeValue,
          variants: source.variants
        })
      : getVariantById(source.variants, selectedVariantId);

    if (variant?.id) input.value = variant.id;
  };

  const setActiveSwatch = (swatch) => {
    const swatchGroup = swatch.closest('.product-card__swatches');
    const card = swatch.closest('.product-card');
    const selectedColorText = card?.querySelector('[data-product-card-selected-color-text]');

    if (!swatchGroup) return;

    swatchGroup.querySelectorAll(swatchSelector).forEach((groupSwatch) => {
      const isActive = groupSwatch === swatch;
      groupSwatch.classList.toggle('product-card__swatch--active', isActive);
      groupSwatch.setAttribute('aria-pressed', String(isActive));
    });

    if (selectedColorText && swatch.dataset.productCardColorValue) {
      const rawColor = swatch.dataset.productCardColorValue;
      selectedColorText.dataset.swatchLabelRaw = rawColor;
      selectedColorText.textContent = window.WobSwatches?.getSwatchLabel?.(rawColor) || formatSwatchTooltip(rawColor);
    }
  };

  const updateCardImage = (card, imageData) => {
    if (!imageData?.src) return;

    const primaryImage = card.querySelector('[data-product-card-primary-image]');
    const secondaryImage = card.querySelector('.product-card__secondary-image');

    if (!primaryImage) return;

    setImageSource(primaryImage, {
      alt: imageData.alt,
      src: imageData.src,
      srcset: imageData.srcset
    });

    if (secondaryImage) {
      setImageSource(secondaryImage, {
        alt: imageData.hoverAlt || '',
        src: imageData.hoverSrc || imageData.src,
        srcset: imageData.hoverSrcset || imageData.srcset
      });
    }
  };

  const applySwatch = async (swatch) => {
    const card = swatch.closest('.product-card');

    if (!card) return;

    const currentUrl = card.querySelector('[data-product-card-link]')?.getAttribute('href') || '';
    const swatchUrl = swatch.dataset.productCardUrl || '';

    if (swatch.classList.contains('product-card__swatch--active') && (!swatchUrl || currentUrl === swatchUrl)) return;

    const requestId = String(++swatchRequestId);
    const loadingDelay = delay(minimumSwatchLoadingMs);

    card.dataset.productCardSwatchRequest = requestId;
    setCardImageLoading(card, true);
    setActiveSwatch(swatch);
    updateCardLinks(card, swatch.dataset.productCardUrl);

    try {
      const [imageData] = await Promise.all([
        getSwatchImage(swatch),
        updateQuickAddForSwatch(card, swatch, requestId),
        hydrateCarouselMedia(card, swatch)
      ]);

      if (card.dataset.productCardSwatchRequest !== requestId) return;
      await Promise.all([preloadSwatchImages(card, imageData), loadingDelay]);
      if (card.dataset.productCardSwatchRequest !== requestId) return;
      updateCardImage(card, imageData);
      resetCarousel(card);
    } finally {
      if (card.dataset.productCardSwatchRequest === requestId) {
        await loadingDelay;
        setCardImageLoading(card, false);
      }
    }
  };

  document.addEventListener('click', (event) => {
    if (!suppressedCarouselClickTarget) return;

    const mediaLink = event.target instanceof Element ? event.target.closest('.product-card__media') : null;

    if (mediaLink && mediaLink === suppressedCarouselClickTarget) {
      event.preventDefault();
      event.stopImmediatePropagation();
      suppressedCarouselClickTarget = null;
      window.clearTimeout(carouselClickSuppressTimer);
    }
  }, true);

  document.addEventListener('click', (event) => {
    const carouselButton = event.target instanceof Element
      ? event.target.closest('[data-product-card-carousel-prev], [data-product-card-carousel-next]')
      : null;

    if (carouselButton) {
      const card = carouselButton.closest('.product-card');
      const state = card ? prepareCarousel(card) : null;
      const direction = carouselButton.matches('[data-product-card-carousel-prev]') ? -1 : 1;

      if (!card || !state?.images?.length) return;

      event.preventDefault();
      void setCarouselImage(card, state.index + direction);
      return;
    }

    const swatchExpand = event.target instanceof Element ? event.target.closest(swatchExpandSelector) : null;

    if (swatchExpand) {
      const swatchGroup = swatchExpand.closest('.product-card__swatches');

      event.preventDefault();
      swatchGroup?.querySelectorAll(`${swatchSelector}[hidden]`).forEach((swatch) => {
        swatch.hidden = false;
      });
      swatchExpand.setAttribute('aria-expanded', 'true');
      swatchExpand.hidden = true;
      return;
    }

    const swatch = event.target instanceof Element ? event.target.closest(swatchSelector) : null;

    if (!swatch) return;

    event.preventDefault();
    applySwatch(swatch);
  });

  document.addEventListener('submit', (event) => {
    const form = event.target instanceof HTMLFormElement
      ? event.target.closest('.product-card [data-cart-add-form]')
      : null;

    if (form) syncQuickAddFormVariant(form);
  }, true);

  document.addEventListener('pointerdown', startCarouselSwipe);
  document.addEventListener('pointermove', moveCarouselSwipe, { passive: false });
  document.addEventListener('pointerup', endCarouselSwipe);
  document.addEventListener('pointercancel', cancelCarouselSwipe);

  document.addEventListener('pointerover', (event) => {
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    const mediaShell = event.target instanceof Element ? event.target.closest(mediaShellSelector) : null;
    const card = mediaShell?.closest('.product-card');

    if (card) prepareCarousel(card);

    const target = getTooltipTarget(event.target);

    if (target) showSwatchTooltip(target);
  });

  document.addEventListener('pointerout', (event) => {
    const target = getTooltipTarget(event.target);
    const relatedTarget = event.relatedTarget;

    if (!target) return;
    if (relatedTarget instanceof Element && target.contains(relatedTarget)) return;

    hideSwatchTooltip(target);
  });

  document.addEventListener('focusin', (event) => {
    const mediaShell = event.target instanceof Element ? event.target.closest(mediaShellSelector) : null;
    const card = mediaShell?.closest('.product-card');

    if (card) prepareCarousel(card);

    const target = getTooltipTarget(event.target);

    if (target) showSwatchTooltip(target);
  });

  document.addEventListener('focusout', (event) => {
    const target = getTooltipTarget(event.target);
    const relatedTarget = event.relatedTarget;

    if (!target) return;
    if (relatedTarget instanceof Element && target.contains(relatedTarget)) return;

    hideSwatchTooltip(target);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      hideSwatchTooltip();
      return;
    }

    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

    const mediaShell = event.target instanceof Element ? event.target.closest(mediaShellSelector) : null;
    const card = mediaShell?.closest('.product-card');
    const state = card ? prepareCarousel(card) : null;

    if (!card || !state?.images?.length) return;

    event.preventDefault();
    void setCarouselImage(card, state.index + (event.key === 'ArrowLeft' ? -1 : 1));
  });

  window.addEventListener('resize', scheduleTooltipPosition);
  window.addEventListener('scroll', scheduleTooltipPosition, true);

  formatSwatchTooltips();

  const tooltipObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof Element)) return;

        formatSwatchTooltips(node);
      });
    });
  });

  tooltipObserver.observe(document.documentElement, {
    childList: true,
    subtree: true
  });
})();
